import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TileDeFato } from "../componentes/CalendarioUi";
import { Cabine } from "../componentes/FerramentaUi";
import { CalendarioMensal, ListaDaAgenda } from "../componentes/PeritoAgenda";
import { PrazoDialogo } from "../componentes/PeritoDialogos";
import { Alternador, Atalho } from "../componentes/PeritoUi";
import { construirAgenda, eventosEntre, proximosDias } from "../lib/perito/agenda";
import { diasAte, hoje } from "../lib/perito/datas";
import { plural } from "../lib/perito/format";
import { usePerito } from "../lib/perito/store";
import type { Prazo } from "../lib/perito/tipos";

// A agenda da Central do Perito (fase B), portada de `src/routes/_authenticated/prazos.tsx`: os prazos
// cadastrados, a entrega do laudo, as tarefas com prazo, os recebimentos previstos e os lembretes, no
// calendário do mês ou nas janelas de hoje, 7 e 30 dias. A visão, o mês e o dia escolhido ficam no ENDEREÇO
// (`?visao=30`, `?mes=2026-12&dia=2026-12-04`): o aluno volta pelo navegador e manda o link de um mês.

const RAIZ = "/ferramenta/central-do-perito";
const VISOES = [
  { id: "calendario", rotulo: "Calendário" },
  { id: "hoje", rotulo: "Hoje" },
  { id: "7", rotulo: "7 dias" },
  { id: "30", rotulo: "30 dias" },
] as const;
type Visao = (typeof VISOES)[number]["id"];

const MES = /^\d{4}-(0[1-9]|1[0-2])$/;
const DIA = /^\d{4}-\d{2}-\d{2}$/;

export default function PeritoPrazos() {
  const { pericias, prazos, tarefas, podeEscrever } = usePerito();
  const [query, setQuery] = useSearchParams();
  const [editando, setEditando] = useState<Prazo | null>(null);
  const [criando, setCriando] = useState(false);

  const hojeSP = hoje();
  const visao: Visao = (VISOES.find((v) => v.id === query.get("visao"))?.id ?? "calendario") as Visao;
  const mes = MES.test(query.get("mes") ?? "") ? query.get("mes")! : hojeSP.slice(0, 7);
  const diaDaQuery = query.get("dia") ?? "";
  // O dia escolhido é de dentro do mês mostrado; fora dele (o mês mudou), vale hoje, se hoje é do mês, senão o dia 1.
  const dia = DIA.test(diaDaQuery) && diaDaQuery.startsWith(mes) ? diaDaQuery : hojeSP.startsWith(mes) ? hojeSP : `${mes}-01`;

  const mudar = (mudancas: Record<string, string | null>) => {
    const nova = new URLSearchParams(query);
    for (const [k, v] of Object.entries(mudancas)) (v ? nova.set(k, v) : nova.delete(k));
    setQuery(nova, { replace: true });
  };

  const eventos = useMemo(() => construirAgenda(pericias, prazos, tarefas), [pericias, prazos, tarefas]);
  const janela = useMemo(() => {
    if (visao === "calendario") return [];
    if (visao === "hoje") return eventosEntre(eventos, hojeSP, hojeSP);
    const { de, ate } = proximosDias(visao === "7" ? 7 : 30);
    return eventosEntre(eventos, de, ate);
  }, [eventos, visao, hojeSP]);

  const numeros = useMemo(() => {
    const dias = prazos.filter((p) => !p.cumprido).map((p) => diasAte(p.data)).filter((d): d is number => d !== null);
    return { abertos: dias.length, semana: dias.filter((d) => d >= 0 && d <= 7).length, vencidos: dias.filter((d) => d < 0).length };
  }, [prazos]);

  // Sem o curso, a conta lê e não grava: o "Editar" não aparece, senão a recusa viria da fila.
  const editarPrazo = podeEscrever ? (id: string) => {
    const p = prazos.find((x) => x.id === id);
    if (p) setEditando(p);
  } : undefined;

  return (
    <div className="flex flex-col gap-5" data-prazos>
      <Cabine rotulo="Agenda" titulo="Prazos"
        descricao="Os prazos, a entrega do laudo, as tarefas, os recebimentos previstos e os lembretes de cada perícia, numa agenda só."
        acao={podeEscrever ? <button type="button" onClick={() => setCriando(true)} className="pill-cta inline-flex min-h-11 items-center px-5 text-sm" data-novo-prazo>Novo prazo</button> : undefined}
        fatos={numeros.abertos > 0 ? (
          <>
            <TileDeFato rotulo="Em aberto" valor={<span data-fato="abertos">{numeros.abertos}</span>} apoio="prazos a cumprir" />
            <TileDeFato rotulo="Nesta semana" valor={<span data-fato="semana">{numeros.semana}</span>} apoio="nos próximos 7 dias" />
            {numeros.vencidos > 0 && <TileDeFato rotulo="Vencidos" valor={<span data-fato="vencidos">{numeros.vencidos}</span>} apoio={numeros.vencidos === 1 ? "pede ação agora" : "pedem ação agora"} />}
          </>
        ) : undefined} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Alternador rotulo="Período" opcoes={VISOES} valor={visao} aoMudar={(v) => mudar({ visao: v === "calendario" ? null : v })} />
        <Atalho para={`${RAIZ}/tarefas`}>Ver as tarefas</Atalho>
      </div>

      {visao === "calendario" ? (
        <CalendarioMensal raiz={RAIZ} eventos={eventos} pericias={pericias} mes={mes}
          aoMudarMes={(m) => mudar({ mes: m === hojeSP.slice(0, 7) ? null : m, dia: null })}
          dia={dia} aoEscolherDia={(d) => mudar({ mes: d.slice(0, 7) === hojeSP.slice(0, 7) ? null : d.slice(0, 7), dia: d === hojeSP ? null : d })}
          aoEditarPrazo={editarPrazo} />
      ) : (
        <ListaDaAgenda raiz={RAIZ} eventos={janela} pericias={pericias} aoEditarPrazo={editarPrazo}
          vazio={`Nada ${visao === "hoje" ? "para hoje" : `nos próximos ${visao} dias`}. Informe a entrega do laudo, os prazos, as tarefas e os honorários com vencimento para vê-los aqui.`} />
      )}

      <p className="text-xs text-cinza">
        Os lembretes aparecem 30, 15, 7, 3 e 1 dia antes de cada prazo em aberto e da entrega do laudo. {plural(eventos.length, "compromisso", "compromissos")} na agenda.
      </p>

      <PrazoDialogo prazo={editando} aberto={!!editando || criando} aoFechar={() => { setEditando(null); setCriando(false); }} />
    </div>
  );
}
